import { BaseViewStore } from 'core/stores';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import _ from 'lodash';
import { AlertsService } from 'common/data';
import { isSuperAdminOrganization } from 'core/utils';

class AlertsViewStore extends BaseViewStore {
	@observable allScheduledJobs = [];
	@observable runningJobs = [];
	@observable fetchRunningJobsIntervalId = null;
	@observable fetchRunningJobsInterval = 5 * 1000;
	@observable fetchAllScheduledJobLogsIntervalId = null;
	@observable fetchAllScheduledJobLogsInterval = 60 * 1000;
	@observable deployments = [];
	@observable jobStatuses = new Map();
	@observable scheduledJobEstimates = new Map();

	scheduledJobLogListViewStore = null;

	setScheduledJobLogListViewStore = store => {
		this.scheduledJobLogListViewStore = store;
		// Start polling for previous jobs every minute
		if (!this.fetchAllScheduledJobLogsIntervalId) {
			this.scheduledJobLogListViewStore.findScheduledJobLogs();
			this.fetchAllScheduledJobLogsIntervalId = setInterval(() => {
				this.scheduledJobLogListViewStore.findScheduledJobLogs();
			}, this.fetchAllScheduledJobLogsInterval);
		}
	};

	constructor(props) {
		super(props);
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		this.alertsService = new AlertsService(this.rootStore.app.baasic.apiClient);
		await this.getScheduledJobEstimates();
		this.startPolling(this.getRunningJobs, this.fetchRunningJobsInterval);
		this.isSuperAdminOrganization = isSuperAdminOrganization(this.rootStore);
		if (this.isSuperAdminOrganization) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this, false);
		}
		this.rootStore.viewStore.headerSetButton();
		this.resume();
	};

	@action.bound
	getRunningJobs = async () => {
		const result = await this.alertsService.getRunningJobs();
		const currentJobsList = result.data;
		runInAction(() => {
			this.runningJobs = currentJobsList;
			this.trackJobCompletions(currentJobsList);
		});
	};

	@action.bound
	startPolling(func, interval) {
		if (this.fetchRunningJobsIntervalId) return;
		func();
		this.fetchRunningJobsIntervalId = setInterval(func, interval);
	}

	@action.bound
	stopPolling() {
		if (this.fetchRunningJobsIntervalId) {
			clearInterval(this.fetchRunningJobsIntervalId);
			this.fetchRunningJobsIntervalId = null;
		}
		if (this.fetchAllScheduledJobLogsIntervalId) {
			clearInterval(this.fetchAllScheduledJobLogsIntervalId);
			this.fetchAllScheduledJobLogsIntervalId = null;
		}
	}

	@action.bound
	trackJobCompletions = currentJobsList => {
		let hasFinishedJob = false;

		const currentJobIds = new Set(currentJobsList.map(job => job.id));

		// detect Finished Jobs (if a job is in the Map but NOT in the new list, it finished)
		for (const [jobId] of this.jobStatuses) {
			if (!currentJobIds.has(jobId)) {
				this.jobStatuses.delete(jobId);
				hasFinishedJob = true;
			}
		}

		// add running jobs to the map for tracking
		currentJobsList.forEach(job => {
			if (!this.jobStatuses.has(job.id)) {
				this.jobStatuses.set(job.id, job);
			}
		});

		if (hasFinishedJob && this.scheduledJobLogListViewStore) {
			// Fetch previous jobs immediately when a job finishes
			this.scheduledJobLogListViewStore.findScheduledJobLogs();
		}
	};

	@action.bound
	getScheduledJobEstimates = async () => {
		const result = await this.alertsService.getScheduledJobEstimates();
		const map = new Map();
		Object.entries(result.data).forEach(([key, value]) => {
			map.set(key.toLowerCase(), this.parseTimeSpanToMs(value));
		});
		this.scheduledJobEstimates = map;
	};

	parseTimeSpanToMs = timeSpan => {
		const [hms, fraction] = timeSpan.split('.');
		const [h, m, s] = hms.split(':').map(Number);
		const msFromFraction = fraction ? Math.round(Number('0.' + fraction) * 1000) : 0;
		return h * 3600000 + m * 60000 + s * 1000 + msFromFraction;
	};

	getEstimatedFinishTime = job => {
		if (!job.startedAt) return null;
		const estimateMs = this.scheduledJobEstimates.get(job.jobTitle.toLowerCase());
		if (!estimateMs) return null;
		const startTime = new Date(job.startedAt).getTime();
		const finishTime = new Date(startTime + estimateMs);
		return finishTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	};

	// returns jobs with their respective estimated time to finish
	@computed
	get activeJobsWithEstimates() {
		return this.runningJobs.map(job => {
			return {
				...job,
				estimatedFinishTime: this.getEstimatedFinishTime(job),
			};
		});
	}

	@action.bound
	destroy() {
		this.stopPolling();
	}
}

export default AlertsViewStore;
